import { Link } from 'react-router-dom';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  Icon,
  LevelBadge,
  Loading,
  Pill,
  StatTile,
} from '../components/ui';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import {
  age,
  money,
  monthLabel,
  moneyCompact,
  number,
  orderStatusLabel,
  orderStatusTone,
  percent,
} from '../lib/format';
import type { DashboardOverview } from '../lib/types';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function Dashboard() {
  const { user, can } = useAuth();
  const { data, error, loading, reload } = useApi<DashboardOverview>('/dashboard');

  const firstName = user?.name.split(' ')[0] ?? '';

  return (
    <>
      <PageHeader
        breadcrumb={['Portfolio', 'Dashboard']}
        title={`${greeting()}, ${firstName}`}
        search
        actions={
          <>
            <button type="button" className="iconbtn" aria-label="Notifications">
              {Icon.bell({ size: 17 })}
            </button>
            {can('application.write') && (
              <Link to="/applications/new">
                <Button variant="primary" icon={Icon.plus({ size: 14 })}>
                  New application
                </Button>
              </Link>
            )}
          </>
        }
      />

      <ScopeBar>
        {data ? (
          <>
            Viewing <strong>{data.scope.nodeName}</strong> — {data.scope.agents} agents,{' '}
            {number(data.scope.merchants)} merchants
          </>
        ) : (
          <>Loading your scope…</>
        )}
      </ScopeBar>

      <PageBody>
        {loading && <Loading label="Loading your portfolio…" />}
        {error && <ErrorState error={error} onRetry={reload} />}

        {data && (
          <>
            <div className="tilerow">
              <StatTile
                label="Applications in flight"
                value={number(data.tiles.applicationsInFlight)}
                badge={
                  data.tiles.needsYourAction > 0
                    ? `${data.tiles.needsYourAction} on you`
                    : undefined
                }
                badgeTone="warn"
                footnote={
                  data.needsAttention[0]
                    ? `Oldest has waited ${age(data.needsAttention[0].lastStageChangeAt)}`
                    : 'Nothing waiting on you'
                }
              />
              <StatTile
                label="Approved this month"
                value={number(data.tiles.approvedThisMonth)}
                footnote="Approval rate, trailing 90 days"
              />
              <StatTile
                label="Active merchants"
                value={number(data.tiles.activeMerchants)}
                badge={
                  data.tiles.boardingMerchants > 0
                    ? `+${data.tiles.boardingMerchants}`
                    : undefined
                }
                badgeTone="ok"
                footnote="Boarding now"
              />
              {data.tiles.residual ? (
                <StatTile
                  label={`Residual · ${monthLabel(data.tiles.residual.period).split(' ')[0]}${
                    data.tiles.residual.paidAt ? ', paid' : ''
                  }`}
                  value={money(data.tiles.residual.amountCents)}
                  badge={
                    data.tiles.residual.changePercent !== null
                      ? `${data.tiles.residual.changePercent > 0 ? '▲' : '▼'} ${percent(
                          Math.abs(data.tiles.residual.changePercent),
                        )}`
                      : undefined
                  }
                  badgeTone={
                    (data.tiles.residual.changePercent ?? 0) >= 0 ? 'ok' : 'danger'
                  }
                  footnote="vs the month before"
                />
              ) : (
                <StatTile
                  label="Residual"
                  value="—"
                  footnote="No residual visibility on your role"
                />
              )}
            </div>

            <div className="split-2">
              <Card>
                <CardHeader
                  title="Needs your attention"
                  count={data.needsAttention.length}
                  action={
                    <Link to="/applications?filter=needs_action" className="cardlink">
                      Full pipeline →
                    </Link>
                  }
                />
                {data.needsAttention.length === 0 ? (
                  <Empty
                    title="Nothing is waiting on you"
                    detail="Every open file is with underwriting or the merchant."
                  />
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Merchant</th>
                          <th>Submitted by</th>
                          <th>Blocked on</th>
                          <th className="num">Age</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {data.needsAttention.map((a) => (
                          <tr key={a.id}>
                            <td>
                              <div className="cell-primary">{a.dba || 'Untitled draft'}</div>
                              <div className="cell-sub">
                                {a.reference} · MCC {a.mcc || '—'}
                              </div>
                            </td>
                            <td>
                              <span className="cell-person">
                                {a.submittedByUserId === user?.userId
                                  ? 'You'
                                  : a.submittedByName}
                                <LevelBadge level={a.submittedByLevel} />
                              </span>
                            </td>
                            <td>
                              {a.blockedOn && <Pill tone="warn">{a.blockedOn}</Pill>}
                            </td>
                            <td className="num">{age(a.lastStageChangeAt)}</td>
                            <td>
                              <Link to={`/applications/${a.id}`}>
                                <Button size="sm">Open</Button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <div className="stack">
                <Card>
                  <CardHeader title="Start something" />
                  <div className="quicklinks">
                    {can('application.write') && (
                      <Link to="/applications/new" className="quicklink">
                        <span className="quicklink__icon">{Icon.applications()}</span>
                        New merchant application
                      </Link>
                    )}
                    {can('equipment.order') && (
                      <Link to="/equipment" className="quicklink">
                        <span className="quicklink__icon">{Icon.equipment()}</span>
                        Order equipment
                      </Link>
                    )}
                    {can('supplies.order') && (
                      <Link to="/supplies" className="quicklink">
                        <span className="quicklink__icon">{Icon.supplies()}</span>
                        Reorder paper
                      </Link>
                    )}
                  </div>
                </Card>

                <Card>
                  <CardHeader title="Boarding this week" />
                  {data.boardingThisWeek.length === 0 ? (
                    <Empty title="Nothing boarding" />
                  ) : (
                    <ul className="feed">
                      {data.boardingThisWeek.map((m) => (
                        <li className="feed__item" key={m.id}>
                          <span className="feed__dot feed__dot--ok" />
                          <div>
                            <div className="feed__title">{m.dba}</div>
                            <div className="feed__sub">
                              MID {m.mid} · terminal ships on approval
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            </div>

            <div className="split-2">
              {data.downlinePerformance.length > 0 && (
                <Card>
                  <CardHeader
                    title="Downline performance"
                    meta={
                      data.tiles.residual
                        ? monthLabel(data.tiles.residual.period)
                        : undefined
                    }
                    action={
                      can('residual.view') ? (
                        <Link to="/reports" className="cardlink">
                          Open reports →
                        </Link>
                      ) : undefined
                    }
                  />
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Agent</th>
                          <th className="num">Apps</th>
                          <th className="num">Approved</th>
                          <th className="num">Volume</th>
                          <th className="num">Your share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.downlinePerformance.map((a) => (
                          <tr key={a.agentUserId}>
                            <td>
                              <span className="cell-person">
                                <Avatar initials={a.initials} size={24} tone="muted" />
                                <span className="cell-primary">{a.name}</span>
                              </span>
                            </td>
                            <td className="num">{a.applications}</td>
                            <td className="num">{a.approved}</td>
                            <td className="num">{moneyCompact(a.volumeCents)}</td>
                            <td className="num">{money(a.yourShareCents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {data.recentOrders.length > 0 && (
                <Card>
                  <CardHeader title="Recent orders" />
                  <ul className="feed">
                    {data.recentOrders.map((o) => (
                      <li className="feed__item feed__item--split" key={o.reference}>
                        <div>
                          <div className="feed__title">
                            {o.lines
                              .map((l) => `${l.quantity} × ${l.name}`)
                              .join(', ')}
                          </div>
                          <div className="feed__sub">
                            {o.merchantName} · <span className="mono">{o.reference}</span>
                          </div>
                        </div>
                        <Pill tone={orderStatusTone(o.status)}>
                          {orderStatusLabel(o.status)}
                        </Pill>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          </>
        )}
      </PageBody>
    </>
  );
}
